import {
    Column,
    Lookup
} from 'devextreme-react/data-grid';

import { useGetActionRequiredOrders } from "../api/get-action-required-orders";
import { ActionRequiredGridProps } from '../types/ActionRequiredGridProps';
import { Link, useLocation } from 'react-router-dom';
import { LoadPagePanel, SyncDataGrid } from '../../../../components';
import { formatShortTime } from '../../../../utils/date-utils';
import { useAuth } from '../../../../contexts/auth';
import { CompanyTypes } from '../../../../types/enums';

export default function ActionRequiredGrid(props: ActionRequiredGridProps) {
    const orders = useGetActionRequiredOrders(props.actionRequiredID, props.pendingOnly, props.pastDueOnly, props.activeOnly);

    const location = useLocation();
    const returnUrl = encodeURIComponent(location.pathname + location.search);

    const { user } = useAuth();
    const isManufacturer = user?.companyTypeId === CompanyTypes.MFR;
    const isServiceProvider = user?.companyTypeId === CompanyTypes.SERVICE_PROVIDER;
    const urlFolder = isManufacturer ? "manufacturer" : isServiceProvider ? "service-provider" : "distributor";

    return (
        <>
            <SyncDataGrid
                stateStorageKey={urlFolder + '-action-required-orders-' + props.actionRequiredID}
                className={'dx-card wide-card action-required-grid'}
                dataSource={orders.data?.orders}
                keyExpr={['actionRequiredID', 'purchaseOrderID']}
                height="68vh"
            >
                <Column
                    dataField={'poNumber'}
                    caption={'Purchase Order'}
                    dataType="string"
                    cellRender={(row: any) => {
                        return <Link to={`/${urlFolder}/purchase-order/${row.data.purchaseOrderID}/${row.data.actionRequiredID}?returnUrl=${returnUrl}`}>
                            {row.data.poNumber}
                        </Link>;
                    }}
                />
                <Column
                    dataField={'date'}
                    caption={'Order Date'}
                    dataType="date"
                />
                <Column
                    dataField={'actionRequiredID'}
                    caption={'Action Required'}
                    dataType="string"
                    cellRender={(row: any) => {
                        return <Link to={`/${urlFolder}/purchase-order/${row.data.purchaseOrderID}/${row.data.actionRequiredID}?returnUrl=${returnUrl}`}>
                            {row.data.actionRequiredDescription}
                        </Link>;
                    }}
                    editorOptions={{
                        dropDownOptions: {
                            minWidth: 250
                        }
                    }}
                >
                    <Lookup
                        dataSource={orders.data?.actionsRequired}
                        valueExpr={'actionRequiredID'}
                        displayExpr={'description'}
                    />
                </Column>
                <Column
                    dataField={'actionRequiredStatusID'}
                    calculateDisplayValue={'actionRequiredStatus'}
                    caption={'AR Status'}
                    dataType="string"
                    editorOptions={{
                        dropDownOptions: {
                            minWidth: 150,
                        }
                    }}                >
                    <Lookup
                        dataSource={orders.data?.arStatuses}
                        valueExpr={'lookupID'}
                        displayExpr={'displayValue'}
                    />
                </Column>
                <Column
                    dataField={'cfiName'}
                    caption={'CFI Name'}
                    dataType="string"
                />
                <Column
                    dataField={'cfiPhone'}
                    caption={'CFI Phone'}
                    dataType="string"
                    visible={isServiceProvider}
                />
                <Column
                    dataField={'customerEmail'}
                    caption={'Customer Email'}
                    dataType="string"
                    visible={isServiceProvider}
                />
                <Column
                    dataField={'customerPhone'}
                    caption={'Customer Phone'}
                    dataType="string"
                    visible={isServiceProvider}
                />
                <Column
                    dataField={'customerName'}
                    caption={'Customer Name'}
                    dataType="string"
                    visible={isServiceProvider}
                />
                <Column
                    dataField={'scheduledInstallDate'}
                    caption={'Scheduled Install Date'}
                    dataType="date"
                    visible={isServiceProvider}
                />
                <Column
                    dataField={'serviceInitiatedDate'}
                    caption={'Service Initial Date'}
                    dataType="date"
                    visible={isServiceProvider}
                />
                {
                    !isServiceProvider &&
                    <Column
                        dataField={'comPONumber'}
                        caption={'CFI MO Number'}
                        dataType="string"
                    />
                }
                {
                    !isServiceProvider &&
                    <Column
                        dataField={'shipToLocation'}
                        caption={'Ship To'}
                        dataType="string"
                    />
                }
                {
                    !isServiceProvider &&
                    <Column
                        dataField={'requestedDeliveryDate'}
                        caption={'Requested Delivery Date'}
                        dataType="date"
                    />
                }
                {
                    !isServiceProvider &&
                    <Column
                        dataField={'distributorName'}
                        caption={'Distributor'}
                        dataType="string"
                        visible={isManufacturer}
                    />
                }
                <Column
                    dataField={'userName'}
                    caption={'User'}
                    dataType="string"
                    visible={!isServiceProvider}
                />
                <Column
                    dataField={'lastUpdatedDate'}
                    caption={'Date'}
                    dataType="date"
                    visible={!isServiceProvider}
                />
                <Column
                    caption={'Time'}
                    dataType="string"
                    calculateDisplayValue={(row) => { return formatShortTime(row.lastUpdatedDate) }}
                    visible={!isServiceProvider}
                />
            </SyncDataGrid>

            <LoadPagePanel visible={orders.isLoading} />
        </>
    )
} 
